import type { Variants } from "motion/react"
import React, { useEffect, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import hexToRgba from "hex-to-rgba"
import { AnimatePresence, motion } from "motion/react"
import {
	IconTextBoldOutline18 as Bold,
	IconCode2Outline18 as Code2,
	IconLinkOutline18 as Link,
	IconLinkSlashOutline18 as UnlinkIcon,
} from "nucleo-ui-outline-18"
import { create } from "zustand" // move to lib/states

import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { InlineCode, Shortcut, Tooltip } from "@productlane/ui"
import { BlockquoteIcon } from "@productlane/ui/src/icons/project/blockquote-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type {
	IssueSelectIssue,
	ProjectSelectProject,
} from "../threads/components/right-bar/issue-select"
import type { Editor } from "@tiptap/react"
import { BubbleMenuPlugin } from "@/components/editor/extensions/bubble-menu"
import { EditorMenuHeadingDropdown } from "@/components/editor/menu/dropdowns/editor-menu-heading-dropdown"
import { EditorMenuListDropdown } from "@/components/editor/menu/dropdowns/editor-menu-lists-dropdown"
import { EditorMenuButton } from "@/components/editor/menu/editor-menu-button"
import { EditorMenuInput } from "@/components/editor/menu/editor-menu-input"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import { IssueAndProjectSelect } from "../threads/components/right-bar/issue-select"
import { useThreadContext } from "../threads/thread-context"
import { ThreadTagSelect } from "./components/right-bar/thread-tag-select"

function getHighlightedText(id: string) {
	const elements = document.querySelectorAll(`[data-id="${id}"]`)
	const elementsArray = Array.from(elements)

	const selectedText = elementsArray
		.map((element) => element.outerHTML)
		.join("<br/><br/>")

	return selectedText
}

const inputVariants: Variants = {
	hidden: {
		width: 0,
	},
	addingLink: {
		width: 156,
	},
}

interface InsightEditorMenuProps {
	editor: Editor | null
	id?: string
}

interface InsightEditorMenuState {
	isOpen: boolean
	setIsOpen: (value: boolean) => void
}

export const useInsightEditorMenuState = create<InsightEditorMenuState>()(
	(set) => ({
		isOpen: false,
		setIsOpen: (value) => {
			set({ isOpen: value })
		},
	}),
)

export const InsightEditorMenu = React.memo(
	({ editor, id }: InsightEditorMenuProps) => {
		const containerRef = useRef<HTMLDivElement | null>(null)

		const { setIsOpen, isOpen } = useInsightEditorMenuState()
		const zero = useZero()
		const { thread } = useThreadContext()
		const [isAddingLink, setIsAddingLink] = useState(false)
		const [link, setLink] = useState("")
		const element = useRef<HTMLDivElement | null>(null)
		useEffect(() => {
			if (!element.current || !editor) {
				return
			}

			if (editor.isDestroyed) {
				return
			}

			const plugin = BubbleMenuPlugin({
				updateDelay: 500,
				editor,
				element: element.current,
				pluginKey: "bubbleMenu",
				shouldShow: null,
				tippyOptions: {
					maxWidth: 1000,
					duration: 100,
					placement: "top",
					zIndex: 40,
					onShown: () => {
						setIsOpen(true)
					},
					onHidden: () => {
						setIsOpen(false)
					},
				},
			})

			editor.registerPlugin(plugin)
			return () => {
				editor.unregisterPlugin("bubbleMenu")
			}
		}, [editor, setIsOpen])
		const [company] = useQuery(queries.companyById(thread?.companyId ?? ""))

		const selectIssue = async (issue: IssueSelectIssue) => {
			const issueId = issue.type === "local" ? issue.id : generateId()

			editor
				?.chain()
				.focus()
				.setHighlight({
					color: hexToRgba("#06b6d4", "0.2"),
					id: issueId,
				})
				.run()

			const selectedIssueText = getHighlightedText(issueId)

			await mutateOk(
				zero.mutate(
					mutators.issues.link({
						type: issue.type,
						feedbackId: thread.id,
						customerNeed: {
							priority: Number(thread?.painLevel === "HIGH"),
							body: selectedIssueText,
							videoId: thread?.videoId,
							...(!!company && { company: company }),
						},
						issueId,
						linearIssue: {
							id: issue.linearTicketId,
							identifier: issue.identifier,
							priorityLabel: issue.priority,
							projectId: issue.projectId,
							dueDate: issue.dueDate ?? null,
							isVisible: issue.isVisible,
							state: {
								type: issue.status ?? "",
								name: issue.statusName ?? "",
								color: issue.statusColor ?? "",
							},
							team: {
								id: issue.team,
							},
							title: issue.title,
							url: issue.url,
							description: issue.description,
						},
						notUpdateVisibility: true,
					}),
				),
			)
		}

		const selectTag = async (tagId: string, tagColor: string) => {
			editor
				?.chain()
				.focus()
				.setHighlight({
					color: hexToRgba(tagColor, "0.2"),
					id: tagId,
				})
				.run()
			// it toggles linking of tag
			await mutateOk(
				zero.mutate(
					mutators.tags.linkTag({
						threadId: thread.id,
						tagId: tagId,
					}),
				),
			)
			return
		}

		const selectProject = async (project: ProjectSelectProject) => {
			const projectId = project.type === "local" ? project.id : generateId()

			editor
				?.chain()
				.focus()
				.setHighlight({
					color: hexToRgba(project.color ?? "", "0.2"),
					id: projectId,
				})
				.run()

			const selectedProjectText = getHighlightedText(projectId)

			await mutateOk(
				zero.mutate(
					mutators.projects.link({
						type: project.type,
						feedbackId: thread.id,
						customerNeed: {
							priority: Number(thread?.painLevel === "HIGH"),
							body: selectedProjectText,
							videoId: thread?.videoId,
							...(!!company && { company: company }),
						},
						projectId,
						linearProject: {
							id: projectId,
							linearProjectId: project.linearProjectId ?? project.id,
							name: project.name,
							description: project.description,
							icon: project.icon,
							url: project.url || "",
							startDate: null,
							targetDate: project.targetDate ?? null,
							state: project.state,
							color: project.color,
							sortOrder: project.sortOrder ?? 0,
							progress: project.progress,
							isVisible: project.isVisible,
						},
						isVisible: project.isVisible,
						notUpdateVisibility: true,
						createCustomerNeed: true,
					}),
				),
			)
		}

		if (!editor) {
			return null
		}

		return (
			<div id={id ?? "custom-menu"} ref={element}>
				<div>
					{/* {thread && (
						<AiSuggestionSelect
							linkIssue={selectExsitingIssue}
							linkProject={selectProject}
							linkTag={selectTag}
							selectedText={window.getSelection()?.toString()}
							thread={thread}
						/>
					)} */}

					<div
						className="relative mt-1 flex flex-col gap-1 sm:flex-row"
						ref={containerRef}
					>
						<div className="flex h-8 whitespace-nowrap rounded-lg bg-white px-1 py-5 font-medium shadow-sm ring-1 ring-gray-300/30 dark:bg-gray-850 dark:text-gray-200 dark:ring-gray-700/40">
							<AnimatePresence>
								{isAddingLink && (
									<motion.div
										variants={inputVariants}
										animate={isAddingLink ? "addingLink" : "hidden"}
										exit="hidden"
										className="flex items-center"
									>
										<EditorMenuInput
											value={link}
											onChange={(value) => setLink(value)}
											marginBottom={0}
											placeholder="https://productlane.com"
											onEnter={() => {
												editor
													.chain()
													.focus()
													.extendMarkRange("link")
													.setLink({ href: link, target: "_blank" })
													.run()
												setLink("")
												setIsAddingLink(false)
											}}
											onEscape={() => {
												setLink("")
												setIsAddingLink(false)
											}}
										/>
									</motion.div>
								)}
							</AnimatePresence>
							<div className="flex items-center gap-2">
								<EditorMenuHeadingDropdown
									editor={editor}
									containerRef={containerRef}
								/>

								<Tooltip shouldWrapChildren label="Bold">
									<EditorMenuButton
										onClick={(e) => {
											editor.chain().focus().toggleBold().run()
											e.stopPropagation()
											e.preventDefault()
										}}
										isActive={editor.isActive("bold")}
									>
										<Bold className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								{/* <Tooltip shouldWrapChildren label="Italic">
									<EditorMenuButton
										onClick={() => editor.chain().focus().toggleItalic().run()}
										isActive={editor.isActive("italic")}
									>
										<ItalicIcon className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								<Tooltip shouldWrapChildren label="Strikethrough">
									<EditorMenuButton
										onClick={() => editor.chain().focus().toggleStrike().run()}
										isActive={editor.isActive("strike")}
									>
										<StrikethroughIcon className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip> */}

								<Tooltip shouldWrapChildren label="Link">
									<EditorMenuButton
										onClick={() => {
											setIsAddingLink(!isAddingLink)
										}}
										isActive={isAddingLink}
									>
										<Link className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								{editor.isActive("link") && (
									<Tooltip shouldWrapChildren label="Remove link">
										<EditorMenuButton
											onClick={(event) => {
												event.preventDefault()
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.unsetLink()
													.run()
											}}
											isActive={editor.isActive("link")}
										>
											<UnlinkIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>
								)}

								<Tooltip shouldWrapChildren label="Blockquote">
									<EditorMenuButton
										onClick={() =>
											editor
												.chain()
												.focus(undefined, { scrollIntoView: false })
												.toggleBlockquote()
												.run()
										}
										isActive={editor.isActive("blockquote")}
									>
										<BlockquoteIcon className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								<Tooltip shouldWrapChildren label="Inline code">
									<EditorMenuButton
										onClick={() =>
											editor
												.chain()
												.focus(undefined, { scrollIntoView: false })
												.toggleCode()
												.run()
										}
										isActive={editor.isActive("code")}
									>
										<InlineCode className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								<Tooltip shouldWrapChildren label="Code block">
									<EditorMenuButton
										onClick={() =>
											editor
												.chain()
												.focus(undefined, { scrollIntoView: false })
												.toggleCodeBlock()
												.run()
										}
										isActive={editor.isActive("codeBlock")}
									>
										<Code2 className="h-4 w-4" />
									</EditorMenuButton>
								</Tooltip>

								<EditorMenuListDropdown
									editor={editor}
									containerRef={containerRef}
								/>

								<ThreadTagSelect
									variant="editor"
									modal={false}
									closeOnSelect
									isHotkeyEnabled={isOpen}
									onChange={selectTag}
									// dont show selected tags because this would be a bit tricky, do we show selected tags for just the selection or the entire insight
									// could be confusing either way
									tagIds={[]}
									side="bottom"
								/>
								<IssueAndProjectSelect
									variant="editor"
									modal={false}
									editor={editor}
									issueIds={thread.issues.map((i) => i.linearTicketId)}
									onChange={selectIssue}
									side="bottom"
									isHotkeyEnabled={isOpen}
									shortcut="meta+shift+i, ctrl+shift+i"
									shortcutTooltip={
										<div className="flex gap-1">
											<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
											<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
											<Shortcut>{ShortcutSymbols.I}</Shortcut>
										</div>
									}
									showProjects
									projectIds={thread.projects.map((p) => p.id)}
									onProjectSelect={selectProject}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	},
)
